/*
  Gemeinsame Hülle für draggable und resizebare Floating-Panels im Shiftplan V2.
*/

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { IconClose, IconDrag } from "../../../../design-system/icons";

const PANEL_MARGIN = 12;
const BASE_Z_INDEX = 2610;
const DEFAULT_WIDTH = 520;
const DEFAULT_HEIGHT = 520;
const DEFAULT_MIN_WIDTH = 420;
const DEFAULT_MIN_HEIGHT = 340;

let floatingPanelZIndex = BASE_Z_INDEX;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function claimZIndex() {
  floatingPanelZIndex += 1;
  return floatingPanelZIndex;
}

function viewportSize() {
  if (typeof window === "undefined") {
    return { width: 1400, height: 900 };
  }

  return {
    width: Math.max(360, Number(window.innerWidth || 1400)),
    height: Math.max(240, Number(window.innerHeight || 900)),
  };
}

function clampPanelSize(size, { defaultWidth, defaultHeight, minWidth, minHeight, maxWidth, maxHeight }) {
  const viewport = viewportSize();
  const safeMinWidth = Math.max(220, Number(minWidth || DEFAULT_MIN_WIDTH));
  const safeMinHeight = Math.max(180, Number(minHeight || DEFAULT_MIN_HEIGHT));
  const safeMaxWidth = Math.max(safeMinWidth, Math.min(Number(maxWidth || viewport.width), viewport.width - PANEL_MARGIN * 2));
  const safeMaxHeight = Math.max(
    safeMinHeight,
    Math.min(Number(maxHeight || viewport.height), viewport.height - PANEL_MARGIN * 2)
  );

  return {
    width: Math.round(clamp(Number(size?.width || defaultWidth || DEFAULT_WIDTH), safeMinWidth, safeMaxWidth)),
    height: Math.round(clamp(Number(size?.height || defaultHeight || DEFAULT_HEIGHT), safeMinHeight, safeMaxHeight)),
  };
}

function clampPanelPosition(position, size) {
  const viewport = viewportSize();
  const panelWidth = Math.max(0, Number(size?.width || DEFAULT_WIDTH));
  const panelHeight = Math.max(0, Number(size?.height || DEFAULT_HEIGHT));
  const maxX = Math.max(PANEL_MARGIN, viewport.width - panelWidth - PANEL_MARGIN);
  const maxY = Math.max(PANEL_MARGIN, viewport.height - panelHeight - PANEL_MARGIN);

  return {
    x: Math.round(clamp(Number(position?.x || PANEL_MARGIN), PANEL_MARGIN, maxX)),
    y: Math.round(clamp(Number(position?.y || PANEL_MARGIN), PANEL_MARGIN, maxY)),
  };
}

function resolveDefaultPosition(size, defaultPosition) {
  const viewport = viewportSize();
  const fallback = {
    x: viewport.width - Number(size?.width || DEFAULT_WIDTH) - 24,
    y: 88,
  };

  if (typeof defaultPosition === "function") {
    return clampPanelPosition(defaultPosition(size, viewport) || fallback, size);
  }

  if (defaultPosition && typeof defaultPosition === "object") {
    return clampPanelPosition(defaultPosition, size);
  }

  return clampPanelPosition(fallback, size);
}

function buildDefaultLayout(clampOptions, defaultPosition) {
  const size = clampPanelSize(
    {
      width: clampOptions.defaultWidth,
      height: clampOptions.defaultHeight,
    },
    clampOptions
  );

  return {
    size,
    position: resolveDefaultPosition(size, defaultPosition),
  };
}

function readStoredLayout(storageKey, clampOptions, defaultPosition) {
  const fallback = buildDefaultLayout(clampOptions, defaultPosition);
  if (typeof window === "undefined" || !storageKey) return fallback;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return fallback;

    const parsed = JSON.parse(raw);
    const size = clampPanelSize(parsed?.size, clampOptions);
    const position = clampPanelPosition(parsed?.position, size);
    return { size, position };
  } catch (_error) {
    return fallback;
  }
}

function persistStoredLayout(storageKey, layout) {
  if (typeof window === "undefined" || !storageKey) return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(layout));
  } catch (_error) {
    // Layout persistence is best effort; the panel remains usable without it.
  }
}

function resolvePanelLayout({ storageKey, clampOptions, defaultPosition, persistLayout }) {
  return persistLayout
    ? readStoredLayout(storageKey, clampOptions, defaultPosition)
    : buildDefaultLayout(clampOptions, defaultPosition);
}

export default function FloatingPanel({
  open = false,
  title = "",
  subtitle = "",
  icon = null,
  children,
  onClose,
  headerActions = null,
  panelId = "shiftplan_v2_panel",
  layoutVersion = 1,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  minWidth = DEFAULT_MIN_WIDTH,
  minHeight = DEFAULT_MIN_HEIGHT,
  maxWidth = null,
  maxHeight = null,
  defaultPosition = null,
  persistLayout = true,
  className = "",
  bodyClassName = "",
  ariaLabel = "",
}) {
  const clampOptions = useMemo(
    () => ({
      defaultWidth: width,
      defaultHeight: height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
    }),
    [height, maxHeight, maxWidth, minHeight, minWidth, width]
  );
  const parsedLayoutVersion = Number(layoutVersion);
  const resolvedLayoutVersion =
    Number.isFinite(parsedLayoutVersion) && parsedLayoutVersion > 0 ? Math.floor(parsedLayoutVersion) : 1;
  const storageKey = `spv2_floating_panel_layout_${String(panelId || "panel").trim() || "panel"}_v${resolvedLayoutVersion}`;
  const layoutIdentity = `${persistLayout ? "persist" : "volatile"}:${storageKey}`;
  const initialLayout = useMemo(
    () => resolvePanelLayout({ storageKey, clampOptions, defaultPosition, persistLayout }),
    [clampOptions, defaultPosition, persistLayout, storageKey]
  );

  const [size, setSize] = useState(initialLayout.size);
  const [position, setPosition] = useState(initialLayout.position);
  const [interactionState, setInteractionState] = useState(null);
  const [zIndex, setZIndex] = useState(() => (open ? claimZIndex() : BASE_Z_INDEX));
  const interactionRef = useRef(null);
  const layoutIdentityRef = useRef(layoutIdentity);
  const layoutIdentityChanged = layoutIdentityRef.current !== layoutIdentity;

  const bringToFront = useCallback(() => {
    setZIndex((current) => {
      const next = claimZIndex();
      return current === next ? current : next;
    });
  }, []);

  const syncIntoViewport = useCallback(() => {
    setSize((currentSize) => {
      const nextSize = clampPanelSize(currentSize, clampOptions);
      setPosition((currentPosition) => clampPanelPosition(currentPosition, nextSize));
      return nextSize;
    });
  }, [clampOptions]);

  useEffect(() => {
    if (!open) return;
    bringToFront();
    syncIntoViewport();
  }, [bringToFront, open, syncIntoViewport]);

  useEffect(() => {
    if (!open) return undefined;

    const handleResize = () => syncIntoViewport();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [open, syncIntoViewport]);

  useEffect(() => {
    if (!layoutIdentityChanged) return;
    const nextLayout = resolvePanelLayout({ storageKey, clampOptions, defaultPosition, persistLayout });
    layoutIdentityRef.current = layoutIdentity;
    setSize(nextLayout.size);
    setPosition(nextLayout.position);
  }, [clampOptions, defaultPosition, layoutIdentity, layoutIdentityChanged, persistLayout, storageKey]);

  useEffect(() => {
    if (!persistLayout || !open || layoutIdentityChanged) return;
    persistStoredLayout(storageKey, { size, position });
  }, [layoutIdentityChanged, open, persistLayout, position, size, storageKey]);

  useEffect(() => {
    if (!interactionState) return undefined;

    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = interactionState === "resize" ? "nwse-resize" : "grabbing";
    document.body.classList.add("spv2-floating-panel-interacting");

    const handleMouseMove = (event) => {
      const state = interactionRef.current;
      if (!state) return;

      if (state.mode === "drag") {
        setPosition(
          clampPanelPosition(
            {
              x: Number(state.startX || 0) + Number(event.clientX || 0) - Number(state.pointerStartX || 0),
              y: Number(state.startY || 0) + Number(event.clientY || 0) - Number(state.pointerStartY || 0),
            },
            size
          )
        );
        return;
      }

      if (state.mode === "resize") {
        const nextSize = clampPanelSize(
          {
            width: Number(state.startWidth || width) + Number(event.clientX || 0) - Number(state.pointerStartX || 0),
            height: Number(state.startHeight || height) + Number(event.clientY || 0) - Number(state.pointerStartY || 0),
          },
          clampOptions
        );

        setSize(nextSize);
        setPosition((currentPosition) => clampPanelPosition(currentPosition, nextSize));
      }
    };

    const handleMouseUp = () => {
      interactionRef.current = null;
      setInteractionState(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = previousCursor;
      document.body.classList.remove("spv2-floating-panel-interacting");
    };
  }, [clampOptions, height, interactionState, size, width]);

  const handleDragStart = useCallback(
    (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      bringToFront();
      interactionRef.current = {
        mode: "drag",
        pointerStartX: Number(event.clientX || 0),
        pointerStartY: Number(event.clientY || 0),
        startX: position.x,
        startY: position.y,
      };
      setInteractionState("drag");
    },
    [bringToFront, position.x, position.y]
  );

  const handleResizeStart = useCallback(
    (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      bringToFront();
      interactionRef.current = {
        mode: "resize",
        pointerStartX: Number(event.clientX || 0),
        pointerStartY: Number(event.clientY || 0),
        startWidth: Number(size.width || width),
        startHeight: Number(size.height || height),
      };
      setInteractionState("resize");
    },
    [bringToFront, height, size.height, size.width, width]
  );

  if (!open) return null;

  return (
    <Box
      className={`spv2-floating-panel${interactionState ? ` is-${interactionState}` : ""}${className ? ` ${className}` : ""}`}
      role="dialog"
      aria-modal="false"
      aria-label={ariaLabel || title || "Panel"}
      data-panel-id={panelId}
      onMouseDownCapture={bringToFront}
      style={{
        zIndex,
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
      }}
    >
      <Box className="spv2-floating-panel-header">
        <Box className="spv2-floating-panel-header-main" onMouseDown={handleDragStart}>
          <Box className="spv2-floating-panel-drag-icon">
            <IconDrag fontSize="small" />
          </Box>
          {icon ? <Box className="spv2-floating-panel-title-icon">{icon}</Box> : null}
          <Box className="spv2-floating-panel-title-copy">
            <Typography className="spv2-floating-panel-title">{title}</Typography>
            {subtitle ? <Typography className="spv2-floating-panel-subtitle">{subtitle}</Typography> : null}
          </Box>
        </Box>

        <Box className="spv2-floating-panel-header-actions">
          {headerActions}
          <IconButton className="spv2-floating-panel-close-btn" size="small" onClick={onClose} aria-label="Panel schließen">
            <IconClose fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box className={`spv2-floating-panel-body${bodyClassName ? ` ${bodyClassName}` : ""}`}>{children}</Box>

      <Box
        className="spv2-floating-panel-resize-handle"
        role="separator"
        aria-label="Panelgröße anpassen"
        onMouseDown={handleResizeStart}
      />
    </Box>
  );
}
